import AdminUsers from '@layouts/admin/Users';
import AdminApiUsers from '@layouts/admin/ApiUsers';
import DeviceTypePage from '@layouts/admin/DeviceType';
import SuperGroups from '@layouts/admin/SuperGroups';
import GlobalSettings from '@layouts/admin/GlobalSettings';
import Firmwares from '@layouts/firmwares/Firmwares';
import Rules from '@layouts/firmwares/Rules';
import STBGroup from '@layouts/devices/STBGroup';
import ProvisionedDevices from '@layouts/devices/ProvisionedDevices';
import Devices from '@layouts/devices/Devices';
import BatchMove from '@layouts/devices/BatchMove';
import { User } from '@services/types';
import { Role } from '@openapi';
import { matchPath } from 'react-router-dom';
import Jobs from '@layouts/jobs';
import Reports from '@layouts/reports';
import Rescue from '@layouts/firmwares/Rescue';
// import React from 'react';
import ActionHistory from '@layouts/admin/ActionHistory';
import Tags from '@layouts/tags';
import RequestTrackingDashboard from '@layouts/tracking/RequestTrackingDashboard';
export enum Navigation {
  Login = '/login',
  ChangePassword = '/recover-password',
  Saml = '/saml',
  RecoverPassword = '/forgot-password',
  ResetPassword = '/reset-confirm',
  Main = '/main',
  MyProfile = '/main/my-profile',
  Dashboard = '/main/dashboard',
  // Devices
  Devices = '/main/devices',
  UpdateDevice = '/main/devices/update/:id',
  STBGroup = '/main/devices/stb-group',
  STBGroupInfoById = '/main/devices/stb-group/info/:id',
  CreateSTBGroup = '/main/devices/stb-group/create',
  UpdateSTBGroup = '/main/devices/stb-group/update/:id',
  BatchMove = '/main/devices/batch-move',
  ProvisionedDevices = '/main/devices/provisioned-devices',
  // Firmwares
  Firmwares = '/main/firmwares',
  FirmwaresInfo = '/main/firmwares/info/:id',
  CreateFirmware = '/main/firmwares/create',
  DeployFirmware = '/main/firmwares/deploy/:id',
  Rules = '/main/firmwares/rules',
  CreateRule = '/main/firmwares/rules/create',
  UpdateRule = '/main/firmwares/rules/update/:id',
  Rescue = '/main/firmwares/rescue',
  CreateRescue = '/main/firmwares/rescue/create',
  UpdateRescue = '/main/firmwares/rescue/update/:id',
  RescueInfo = '/main/firmwares/rescue/info/:id',
  RuleList = '/main/firmwares/rule-list',
  //Tags
  Tags = '/main/tags',
  // Request Tracking
  RequestTracking = '/main/administration/tracking',
  DeviceTracking = '/main/tracking/device/:macAddress',
  // Jobs
  Jobs = '/main/jobs',
  JobInfo = '/main/job/:id',
  // Reports
  Reports = '/main/reports/:tab',
  GroupMap = '/main/group-map/:id',
  ReportsById = '/main/reports/:tab/:type?/:value?',
  // Administration
  Administration = '/main/administration',
  UserInfo = '/main/administration/info/:id',
  UpdateUser = '/main/administration/update/:id',
  CreateUser = '/main/administration/create',
  ApiUsers = '/main/administration/api-users',
  ApiUserInfo = '/main/administration/api-users/info/:id',
  CreateApiUser = '/main/administration/api-users/create',
  UpdateApiUser = '/main/administration/api-users/update/:id',
  SuperGroup = '/main/administration/super-group',
  DeviceType = '/main/administration/device-type',
  DeviceTypeInfo = '/main/administration/device-type/info/:id',
  CreateDeviceType = '/main/administration/device-type/create',
  UpdateDeviceType = '/main/administration/device-type/update/:id',
  DeviceTypeSettings = '/main/administration/device-type/settings/:id',
  Certificates = '/main/administration/device-type/certificates/:id',
  GlobalSettings = '/main/administration/global-settings',
  ActionHistory = '/main/administration/action-history',
  // Only for debugging
  IncorrectRequests = '/main/incorrect-requests/:period',
}

const UNPROTECTED_ROUTES = [
  Navigation.Login,
  Navigation.ChangePassword,
  Navigation.Saml,
  Navigation.RecoverPassword,
  Navigation.ResetPassword,
];

export const PARTNER_PERMISSIONS = [
  ...UNPROTECTED_ROUTES,
  Navigation.Firmwares,
  Navigation.CreateFirmware,
  Navigation.MyProfile,
];
export const QUALITY_PERMISSIONS = [
  ...UNPROTECTED_ROUTES,
  Navigation.Devices,
  Navigation.Tags,
  Navigation.STBGroup,
  Navigation.STBGroupInfoById,
  Navigation.Reports,
  Navigation.Jobs,
  Navigation.JobInfo,
  Navigation.ReportsById,
  Navigation.IncorrectRequests,
  Navigation.Dashboard,
  Navigation.ProvisionedDevices,
  Navigation.DeviceTracking,
];
export const INTEGRATION_PERMISSIONS = [
  ...QUALITY_PERMISSIONS,
  Navigation.UpdateDevice,
  Navigation.CreateSTBGroup,
  Navigation.UpdateSTBGroup,
  Navigation.Rules,
  Navigation.CreateRule,
  Navigation.UpdateRule,
  Navigation.BatchMove,
  Navigation.Firmwares,
  Navigation.FirmwaresInfo,
  Navigation.DeployFirmware,
];

export const isAccessibleRoute = (user: User | null, route: Navigation): boolean => {
  if (user?.roles.includes(Role.Superadmin)) {
    return true;
  }

  if (matchPath(route, { path: Navigation.Main, exact: true })) {
    return true;
  }

  if (
    user?.roles.includes(Role.Integration) &&
    INTEGRATION_PERMISSIONS.some((link) => matchPath(route, { path: link, exact: true }))
  ) {
    return true;
  }

  if (
    user?.roles.includes(Role.Quality) &&
    QUALITY_PERMISSIONS.some((link) => matchPath(route, { path: link, exact: true }))
  ) {
    return true;
  }

  if (
    user?.roles.includes(Role.Partner) &&
    PARTNER_PERMISSIONS.some((link) => matchPath(route, { path: link, exact: true }))
  ) {
    return true;
  }

  return false;
};

export interface MenuItem {
  label: string;
  link: Navigation;
  subItems?: MenuItem[];
  Component?: React.FC;
}

export const navigation: MenuItem[] = [
  {
    label: 'Devices',
    link: Navigation.Devices,
    subItems: [
      { label: 'Devices', link: Navigation.Devices, Component: Devices },
      { label: 'Provisioned Devices', link: Navigation.ProvisionedDevices, Component: ProvisionedDevices },
      { label: 'Groups', link: Navigation.STBGroup, Component: STBGroup },
      { label: 'Batch Move', link: Navigation.BatchMove, Component: BatchMove },
    ],
  },
  {
    label: 'Firmwares',
    link: Navigation.Firmwares,
    subItems: [
      { label: 'Firmwares', link: Navigation.Firmwares, Component: Firmwares },
      { label: 'Rules', link: Navigation.Rules.replace('/:id', '') as Navigation, Component: Rules },
      { label: 'Rescue', link: Navigation.Rescue, Component: Rescue },
    ],
  },
  { label: 'Tags', link: Navigation.Tags, Component: Tags },
  { label: 'Jobs', link: Navigation.Jobs, Component: Jobs },
  { label: 'Reports', link: Navigation.Reports.replace(':tab', 'sn-mac') as Navigation.Reports, Component: Reports },
  {
    label: 'Administration',
    link: Navigation.Administration,
    subItems: [
      { label: 'Users', link: Navigation.Administration, Component: AdminUsers },
      { label: 'API Users', link: Navigation.ApiUsers, Component: AdminApiUsers },
      { label: 'Scopes', link: Navigation.SuperGroup, Component: SuperGroups },
      { label: 'Device Type', link: Navigation.DeviceType, Component: DeviceTypePage },
      { label: 'Global Settings', link: Navigation.GlobalSettings, Component: GlobalSettings },
      { label: 'Action History', link: Navigation.ActionHistory, Component: ActionHistory },
      { label: 'Request Tracking', link: Navigation.RequestTracking, Component: RequestTrackingDashboard },
    ],
  },
];
